// Diagnósticos · Proyectos: lista filtrable por programa / cliente / estado con el
// progreso por participante. Al abrir uno (?p=<id>) se muestra su detalle.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { getActingOrg } from '@/lib/actingOrg';
import * as api from '../api';
import type { DiagClient, DiagProgram, DiagProject } from '../types';
import { ProjectDetail } from './ProjectDetail';
import {
  Btn, Chip, Empty, ErrorBox, Field, PROPOSITO_LABEL, Spinner, STATUS_LABEL, TIPO_LABEL, fmtDate, inputCls, statusTone,
} from './ui';

const DONE = new Set(['completado', 'analizando', 'analizado']);

function Progress({ counts }: { counts: Record<string, number> }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const done = Object.entries(counts).filter(([k]) => DONE.has(k)).reduce((a, [, n]) => a + n, 0);
  const analyzed = counts.analizado ?? 0;
  const pct = total ? Math.round((100 * done) / total) : 0;
  return (
    <div className="min-w-[120px]">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{done}/{total} terminaron · {analyzed} analizados</div>
    </div>
  );
}

function NewProject({ programs, onClose, onCreated }: { programs: DiagProgram[]; onClose: () => void; onCreated: (p: DiagProject) => void }) {
  const [f, setF] = useState({ client_name: '', name: '', objective: '', program_id: '', tipo: '', enfoque: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const body: Record<string, unknown> = { client_name: f.client_name.trim(), name: f.name.trim(), objective: f.objective };
      if (f.program_id) body.program_id = f.program_id;
      if (f.tipo) body.tipo = f.tipo;
      if (f.enfoque.trim()) body.enfoque = f.enfoque.trim();
      onCreated(await api.createProject(body));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title="Nuevo proyecto" size="lg"
      footer={<div className="flex justify-end gap-2"><Btn onClick={onClose}>Cancelar</Btn><Btn kind="primary" busy={busy} onClick={save} disabled={!f.name.trim() || !f.client_name.trim()}>Crear</Btn></div>}>
      <div className="space-y-3">
        <ErrorBox error={error} />
        <Field label="Programa" hint="El proyecto hereda la plantilla del programa (tipo, enfoque, invitación, entrevistador).">
          <select className={inputCls} value={f.program_id} onChange={set('program_id')}>
            <option value="">(sin programa)</option>
            {programs.filter((p) => p.status !== 'archivado').map((p) => <option key={p.id} value={p.id}>{p.name} · {PROPOSITO_LABEL[p.proposito]}</option>)}
          </select>
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Cliente (organización a diagnosticar)"><input className={inputCls} value={f.client_name} onChange={set('client_name')} /></Field>
          <Field label="Nombre del proyecto"><input className={inputCls} value={f.name} onChange={set('name')} /></Field>
        </div>
        <Field label="Objetivo"><textarea className={inputCls} rows={2} value={f.objective} onChange={set('objective')} /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Tipo" hint="Vacío = el del programa (o capacitación).">
            <select className={inputCls} value={f.tipo} onChange={set('tipo')}>
              <option value="">(heredar)</option>
              {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Enfoque"><input className={inputCls} value={f.enfoque} onChange={set('enfoque')} /></Field>
        </div>
      </div>
    </Modal>
  );
}

export function ProjectsView() {
  const [sp, setSp] = useSearchParams();
  const openId = sp.get('p') ?? '';
  const [programs, setPrograms] = useState<DiagProgram[]>([]);
  const [clients, setClients] = useState<DiagClient[]>([]);
  const [projects, setProjects] = useState<DiagProject[]>([]);
  const [filters, setFilters] = useState({ program_id: sp.get('programa') ?? '', client_id: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [pr, cl, pj] = await Promise.all([
        // Trabajando en otra empresa los programas (de MI org) no aplican: no se ofrecen.
        getActingOrg() ? Promise.resolve({ programs: [] as DiagProgram[] }) : api.listPrograms(true), api.listClients(),
        api.listProjects({
          program_id: filters.program_id && filters.program_id !== 'none' ? filters.program_id : undefined,
          sin_programa: filters.program_id === 'none', client_id: filters.client_id || undefined,
          status: filters.status || undefined,
        }),
      ]);
      setPrograms(pr.programs);
      setClients(cl.clients);
      setProjects(pj.projects);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { if (!openId) void load(); }, [load, openId]);

  const open = (id: string) => { const n = new URLSearchParams(sp); n.set('p', id); setSp(n); };
  const close = () => { const n = new URLSearchParams(sp); n.delete('p'); setSp(n); };
  const totalParticipants = useMemo(() => projects.reduce((a, p) => a + (p.participants_total ?? 0), 0), [projects]);

  if (openId) return <ProjectDetail projectId={openId} onBack={close} programs={programs} />;

  const sel = (k: keyof typeof filters) => (e: { target: { value: string } }) => setFilters((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Proyectos</h2>
          <p className="text-sm text-muted-foreground">{projects.length} proyecto(s) · {totalParticipants} participante(s)</p>
        </div>
        <div className="flex gap-2">
          <Btn onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Btn>
          <Btn kind="primary" onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> Nuevo proyecto</Btn>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <select className={inputCls} value={filters.program_id} onChange={sel('program_id')} aria-label="Programa">
          <option value="">Todos los programas</option>
          <option value="none">Sin programa</option>
          {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className={inputCls} value={filters.client_id} onChange={sel('client_id')} aria-label="Cliente">
          <option value="">Todos los clientes</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className={inputCls} value={filters.status} onChange={sel('status')} aria-label="Estado">
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="activo">Activo</option>
          <option value="cerrado">Cerrado</option>
        </select>
      </div>
      <ErrorBox error={error} />
      {loading ? <Spinner /> : projects.length === 0 ? <Empty>No hay proyectos con estos filtros.</Empty> : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Proyecto</th>
                <th className="px-3 py-2 font-medium">Programa</th>
                <th className="px-3 py-2 font-medium">Tipo</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium">Progreso</th>
                <th className="px-3 py-2 font-medium">Creado</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} onClick={() => open(p.id)} className="cursor-pointer border-t border-border hover:bg-muted/40">
                  <td className="px-3 py-2">
                    <div className="font-medium text-foreground">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.client.name}</div>
                  </td>
                  <td className="px-3 py-2">{p.program ? <Chip tone="primary">{p.program.name}</Chip> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2 text-xs text-foreground">{TIPO_LABEL[p.tipo] ?? p.tipo}</td>
                  <td className="px-3 py-2"><Chip tone={statusTone(p.status)}>{STATUS_LABEL[p.status] ?? p.status}</Chip></td>
                  <td className="px-3 py-2"><Progress counts={p.participants_by_status ?? {}} /></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <NewProject programs={programs} onClose={() => setCreating(false)} onCreated={(p) => { setCreating(false); open(p.id); }} />}
    </div>
  );
}
